import {
  ArrowLeftOutlined,
  CloudUploadOutlined,
  CopyOutlined,
  EditOutlined,
  LoadingOutlined,
  PauseOutlined,
} from '@ant-design/icons';
import {
  Attachments,
  Bubble,
  Sender,
  useXAgent,
  useXChat,
  Welcome,
  XRequest,
} from '@ant-design/x';
import type { MessageInfo } from '@ant-design/x/es/use-x-chat';
import { useRequest } from '@umijs/max';
import {
  App,
  Avatar,
  Button,
  Flex,
  type GetProp,
  type GetRef,
  Space,
  Spin,
  Switch,
} from 'antd';
import cn from 'classnames';
import dayjs from 'dayjs';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAudioPlayer, useVoiceRecognition } from '@/hooks';
import { streamRequest } from '@/services/api';
import { CHAT_SERVICE, getMessages, getTTSStream } from '@/services/chatbot';
import IconFont from '../IconFont';
import { EventCard } from './CardMessage/EventCard';
import { ReportCard } from './CardMessage/ReportCard';
import { DESIGN_GUIDE, HOT_TOPICS, SENDER_PROMPTS } from './config';

import styles from './index.less';

const NEW_CONVERSATION_ID = 'new';
const OVER_WORDS: string[] = ['结束', '发送', 'over'];
const ANSWER_SEPARATOR = '/nnn';

const Independent: React.FC<{
  config?: API.CHAT.ChatConfig;
  user: string;
  conversationId: string;
  onCreateConversation: (id: string, name: string) => void;
}> = ({
  config = {
    bot_name: '森启小助手',
    opening_statement: '你好，我是森启小助手。需要我帮你做点什么？',
  },
  user,
  conversationId,
  onCreateConversation,
}) => {
  const { message: appMessage } = App.useApp();

  const chatListRef = useRef<HTMLDivElement>(null);
  const abortController = useRef<AbortController>(null);
  const conversationIdRef = useRef<string>(conversationId); // 保存最新的 conversationId
  const messageSegmentsRef = useRef<{ [id: string]: string[] }>({}); // 存储当前流式消息的分段内容
  const senderRef = useRef<GetRef<typeof Sender>>(null);

  // ==================== State ====================
  const [tempMessages, setTempMessages] = useState<
    MessageInfo<API.CHAT.AgentMessage>[]
  >([]);

  const [attachmentsOpen, setAttachmentsOpen] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState<
    GetProp<typeof Attachments, 'items'>
  >([]);

  const [inputValue, setInputValue] = useState('');
  const [inputMode, setInputMode] = useState<'text' | 'voice'>('voice');

  // 使用统一的语音识别 Hook
  const voiceRecognition = useVoiceRecognition({
    mode: 'websocket', // 默认使用 WebSocket ASR
    onTranscriptUpdate: (text, isFinal) => {
      console.log('[Chat] 语音识别更新:', text, isFinal);
      setInputValue(text);
    },
    onError: (error) => {
      const errorMsg = typeof error === 'string' ? error : error.message;
      appMessage.error(`语音识别错误: ${errorMsg}`);
    },
    onEnd: (finalText) => {
      console.log('[Chat] 语音识别结束:', finalText);
      if (finalText) {
        onSubmit(finalText);
        resetInput();
      }
    },
    overWords: OVER_WORDS,
  });

  // 使用音频播放器 Hook
  const audioPlayer = useAudioPlayer({
    sampleRate: 24000,
  });

  // ==================== History Messages ====================
  const { loading: historyMessagesLoading } = useRequest(
    () =>
      conversationId && conversationId !== NEW_CONVERSATION_ID
        ? getMessages({ user, conversation_id: conversationId })
        : Promise.resolve({ data: [] }),
    {
      refreshDeps: [conversationId],
      onSuccess: (data) => {
        const welcomeMessage = {
          id: 'welcome',
          // message: {
          //   role: 'assistant',
          //   content: config.opening_statement,
          // },
          message: {
            id: 'welcome',
            query: '',
            answer: config.opening_statement,
            created_at: Math.round(Date.now() / 1000),
            status: 'normal',
          },
          status: 'updating',
        } as const;
        if (!conversationId || conversationId === NEW_CONVERSATION_ID) {
          // 新会话
          if (tempMessages.length > 0) {
            setMessages(tempMessages);
          } else {
            setMessages([welcomeMessage]);
            audioPlayer.playAudio(
              'welcome',
              undefined,
              config.opening_statement,
              synthesizeStream,
            );
          }
          return;
        }
        const messages = data.map(
          (item) =>
            ({
              id: item.id,
              message: item,
              status: 'local',
            }) as const,
        );
        setMessages([welcomeMessage, ...messages]);
        // const messages: MessageInfo<API.CHAT.ChatMessage>[] = [];
        // data.forEach((item) => {
        //   if (item.query) {
        //     messages.push({
        //       id: item.id,
        //       message: {
        //         role: 'user',
        //         content: item.query,
        //       },
        //       status: 'success',
        //     });
        //   }
        //   if (item.answer) {
        //     // 拆分为多条回复
        //     // const answerList = item.answer.split(ANSWER_SEPARATOR);
        //     // answerList.forEach((answer, index) => {
        //     //   const id = `${item.id}-${index}`;
        //     //   messages.push({
        //     //     id,
        //     //     message: {
        //     //       role: 'assistant',
        //     //       content: answer,
        //     //       audio: audioPlayer.getCachedAudio(id) || '',
        //     //     },
        //     //     status: 'success',
        //     //   });
        //     // });
        //     messages.push({
        //       id: item.id,
        //       message: {
        //         role: 'assistant',
        //         content: item.answer,
        //         audio: audioPlayer.getCachedAudio(item.id) || '',
        //       },
        //       status: 'success',
        //     });
        //   }
        // });
      },
    },
  );

  // ==================== Runtime ====================
  const [agent] = useXAgent<API.CHAT.AgentMessage>({
    request: async (info, callbacks) => {
      if (!info) return;
      return XRequest({
        baseURL: CHAT_SERVICE,
        fetch: (url, options) => streamRequest(url, {}, options),
      }).create<API.CHAT.ChatInput>(
        {
          user,
          conversation_id:
            conversationIdRef.current === NEW_CONVERSATION_ID
              ? ''
              : conversationIdRef.current,
          query: info.message?.query || '',
          response_mode: 'streaming',
          inputs: {},
        },
        {
          onSuccess: (data) => {
            console.log('[XRequest] Success:', Date.now(), data);
            callbacks.onSuccess(data);
          },
          onError: (error) => {
            console.error('[XRequest] Error:', Date.now(), error);
            callbacks.onError(error);
          },
          onUpdate: (msg) => {
            console.log('[XRequest] Update:', Date.now(), msg);
            callbacks.onUpdate(msg);
          },
        },
      );
    },
    // request: async (info, { onUpdate, onSuccess, onError }) => {
    //   try {
    //     const { message, conversationId } = info;

    //     // 使用 sendMessageStream 获取 ReadableStream
    //     const readableStream = await sendMessageStream({
    //       user,
    //       conversation_id:
    //         conversationId === NEW_CONVERSATION_ID ? '' : conversationId,
    //       query: message?.content as string,
    //     });

    //     const chunks: Record<string, any>[] = [];

    //     // 使用 XStream 处理流式数据
    //     for await (const chunk of XStream({
    //       readableStream,
    //     })) {
    //       console.log('[Chat] Chunk Received:', chunk);
    //       // 保存所有 chunks
    //       chunks.push(chunk);
    //       // 调用 onUpdate 更新界面（每次收到新 chunk 时）
    //       // await new Promise((resolve) => setTimeout(resolve, 1000));
    //       onUpdate(chunk);
    //     }

    //     // 流结束后调用 onSuccess，传递所有 chunks
    //     console.log('[Chat] Stream Ended:', chunks);
    //     onSuccess(chunks);
    //   } catch (error) {
    //     console.error('[Chat] Request Failed:', error);
    //     onError(error as Error);
    //   }
    // },
  });

  const { onRequest, messages, parsedMessages, setMessages } = useXChat<
    API.CHAT.AgentMessage,
    API.CHAT.ChatMessage,
    API.CHAT.AgentMessage
  >({
    agent,
    parser: (info) => {
      // console.log('[Chat] Parser:', info);
      const parsedMessage: API.CHAT.ChatMessage[] = [];
      if (info.query) {
        parsedMessage.push({
          type: 'text',
          role: 'user',
          content: info.query,
        });
      }
      if (info.answer && typeof info.answer === 'string') {
        const contentList = info.answer.split(ANSWER_SEPARATOR);
        parsedMessage.push(
          ...contentList.map((content) => {
            try {
              const json = JSON.parse(content);
              if (typeof json === 'object') {
                return {
                  type: 'card',
                  role: 'assistant',
                  content: '卡片消息',
                  data: json,
                } as const;
              }
            } catch (error) {}
            return {
              type: 'text',
              role: 'assistant',
              content,
            } as const;
          }),
        );
      }
      return parsedMessage;
    },
    requestPlaceholder: () => {
      return {
        id: 'placeholder',
        query: '',
        answer: (
          <Space size={10}>
            <Spin indicator={<LoadingOutlined spin />} size="small" />
            加载中…
          </Space>
        ),
        created_at: Math.round(Date.now() / 1000),
        status: 'loading',
      };
    },
    requestFallback: (_, { error }) => {
      console.error(error);
      if (error.name === 'AbortError') {
        return {
          id: 'error',
          query: '',
          answer: '请求中断',
          created_at: Math.round(Date.now() / 1000),
          status: 'error',
        };
      }
      return {
        id: 'error',
        query: '',
        answer: '请求失败，请重试',
        created_at: Math.round(Date.now() / 1000),
        status: 'error',
      };
    },
    transformMessage: (info) => {
      console.log('[Chat] Transform Message:', info);
      const { originMessage, chunk, chunks, status } = info || {};

      // 累积所有消息内容和音频
      let fullContent = originMessage?.answer || '';
      let audioData = originMessage?.audio || '';
      let messageId = originMessage?.id || '';

      if (chunk && chunk.data) {
        try {
          const data = JSON.parse(chunk.data) as API.CHAT.SSEOutput;
          console.log('[Chat] Chunk Data:', data);

          if (data) {
            if (data.message_id) messageId = data.message_id;
            // 更新当前 conversationId
            if (data.conversation_id !== conversationId) {
              let conversationName = '新会话1';
              if (
                data.event === 'workflow_started' &&
                data.data.inputs['sys.query']
              ) {
                conversationName = data.data.inputs['sys.query'];
                onCreateConversation(data.conversation_id, conversationName);
              }
            }

            if (data.event === 'message' || data.event === 'agent_message') {
              // dify 流式响应的不同事件类型
              // 消息内容片段
              const content = data.answer || '';
              fullContent += content;

              // 检测分隔符并进行分段
              const segments = content
                .split(ANSWER_SEPARATOR)
                .map((s) => s.trim());
              let segmentIndex = 0;
              if (messageSegmentsRef.current[messageId]) {
                const lastSegment =
                  messageSegmentsRef.current[messageId].filter(Boolean);
                segmentIndex = lastSegment.length;
                messageSegmentsRef.current[messageId] = [
                  ...lastSegment,
                  ...segments,
                ];
              } else {
                messageSegmentsRef.current[messageId] = segments;
              }
              // 如果存在分隔符，播放最后一个分隔符前面的内容
              if (content.includes(ANSWER_SEPARATOR)) {
                const newSegments = segments.slice(0, -1);
                const id =
                  newSegments.length === 1
                    ? `${messageId}_${segmentIndex + 1}`
                    : `${messageId}_${segmentIndex + 1}-${
                        segmentIndex + newSegments.length
                      }`;
                // 播放分段消息音频
                audioPlayer.playAudio(
                  id,
                  undefined,
                  newSegments.join('。'),
                  synthesizeStream,
                );
              }

              console.log('[Chat] Message:', content, fullContent);
            } else if (data.event === 'message_end') {
              // 消息结束事件
              const segments = messageSegmentsRef.current[messageId];
              if (fullContent === '') {
                fullContent = '未收到回复内容';
              } else if (segments && segments[segments.length - 1]) {
                // 播放最后一个片段
                audioPlayer.playAudio(
                  messageId + '_' + segments.length,
                  undefined,
                  segments[segments.length - 1],
                  synthesizeStream,
                );
              } else if (typeof fullContent === 'string') {
                audioPlayer.playAudio(
                  messageId + '_1',
                  undefined,
                  fullContent,
                  synthesizeStream,
                );
              }
              console.log(
                '[Chat] Message End:',
                messageSegmentsRef.current[messageId] || fullContent,
              );
            } else if (data.event === 'tts_message') {
              // TTS 音频片段
              const audio = data.audio || '';
              audioData += audio;
              console.log(
                '[Chat] TTS Message:',
                audio.substring(0, 50) + '...',
              );
            } else if (data.event === 'tts_message_end') {
              // TTS 音频结束，自动播放
              console.log(
                '[Chat] TTS Message End, total audio length:',
                audioData.length,
              );
              if (audioData) {
                // 自动播放音频
                audioPlayer.setCachedAudio(messageId, audioData);
                audioPlayer.playAudio(messageId, audioData);
              }
            } else if (data.event === 'error') {
              // 出错
              console.log('[Chat] Error:', data);
              appMessage.error(data.message);
            }
          }
        } catch (parseError) {
          console.error('[Chat] Chunk Data Parse Error:', parseError);
        }
      } else if (chunks && chunks.length > 0) {
        // for (const c of chunks) {
        //   if (c.data) {
        //     const data = JSON.parse(c.data);
        //     console.log('[Chat] Chunk Data:', data);
        //   }
        // }
      }

      return {
        // content: fullContent,
        // role: 'assistant',
        // audio: audioData,
        id: messageId,
        query: originMessage?.query || '',
        answer: fullContent,
        created_at: originMessage?.created_at || Math.round(Date.now() / 1000),
        status,
      };
    },
    resolveAbortController: (controller) => {
      abortController.current = controller;
    },
  });

  // 保持 conversationIdRef 为最新值
  useEffect(() => {
    conversationIdRef.current = conversationId;
  }, [conversationId]);

  // ==================== Event ====================
  const onSubmit = (val: string) => {
    if (!val) return;

    if (agent.isRequesting()) {
      appMessage.error('请求处理中，请稍后');
      return;
    }

    if (voiceRecognition.isListening) {
      // 停止录音会再次触发 onSubmit，防止重复触发
      voiceRecognition.stop();
    } else {
      onRequest({
        query: val,
      });
    }
  };
  const resetInput = () => {
    setInputValue('');
  };

  // 当输入模式切换时，停止语音识别
  useEffect(() => {
    if (inputMode === 'text') {
      if (voiceRecognition.isListening) {
        voiceRecognition.stop();
      }
    }
  }, [inputMode, voiceRecognition]);

  useEffect(() => {
    console.log('[Chat] Messages:', messages);
    if (!conversationIdRef.current && messages.length > 0) {
      setTempMessages(messages);
    }
    if (chatListRef.current) {
      chatListRef.current.scrollTo({
        top: chatListRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [messages]);

  const copyText = (content: string) => {
    navigator.clipboard.writeText(content);
    appMessage.success('复制成功');
  };

  // 合成流式音频
  async function synthesizeStream(messageId: string, text: string) {
    const stream = await getTTSStream(text);

    if (!stream) return;

    const reader = stream.getReader();
    const decoder = new TextDecoder();
    const audioChunks = [];
    const base64Chunks: string[] = [];
    let buffer = ''; // 用于处理跨chunk的不完整行

    while (true) {
      const { done, value } = await reader.read();

      if (done) {
        break;
      }

      const chunk = decoder.decode(value, { stream: true });
      buffer += chunk;

      // 按行分割，保留最后一个可能不完整的行
      const lines = buffer.split('\n');
      buffer = lines.pop() || ''; // 保存最后一个可能不完整的行

      lines.forEach((line) => {
        if (line.startsWith('data:')) {
          const data = line.substring(5).trim(); // 去除首尾空白字符
          if (data === '合成完成' || data === '') {
            return;
          }

          // 验证base64字符串格式
          if (!/^[A-Za-z0-9+/]*={0,2}$/.test(data)) {
            console.warn('[TTS] 无效的base64格式:', data.substring(0, 50));
            return;
          }

          // 缓存base64数据
          base64Chunks.push(data);

          // 解码并实时播放（流式）
          try {
            audioPlayer.playPcm(messageId, data, { streaming: true });
          } catch (err) {
            console.error('[TTS] Base64解码错误:', {
              error: err,
              dataLength: data.length,
              dataPreview: data.substring(0, 50),
            });
          }
        }
      });
    }

    // 缓存base64音频数据
    if (base64Chunks.length > 0) {
      audioPlayer.setCachedAudio(messageId, base64Chunks.join(''));
    }
  }

  // ==================== Nodes ====================
  const chatList = (
    <div ref={chatListRef} className={styles['chat-list']}>
      <Spin spinning={historyMessagesLoading}>
        <Bubble.List
          items={parsedMessages?.map((i) => {
            // 保存音频到缓存中
            if (i.message.audio) {
              audioPlayer.setCachedAudio(i.id as string, i.message.audio);
            }

            return {
              ...i.message,
              classNames: {
                content:
                  i.status === 'loading'
                    ? styles['loading-message']
                    : i.message.type === 'card'
                      ? styles['card-message']
                      : '',
              },
              shape: 'round',
              loading: i.status === 'loading' || !i.message.content,
              typing: i.status === 'local' ? false : { step: 5, interval: 30 },
              onTypingComplete: () => {
                if (chatListRef.current) {
                  chatListRef.current.scrollTo({
                    top: chatListRef.current.scrollHeight,
                    behavior: 'smooth',
                  });
                }
              },
              // 为 assistant 消息添加自定义 footer
              ...(i.message.role === 'assistant' &&
                i.message.type !== 'card' && {
                  footer:
                    i.status === 'loading' ? null : (
                      <Flex>
                        <Button
                          type="text"
                          size="small"
                          icon={
                            audioPlayer.loadingId === i.id ? (
                              <LoadingOutlined />
                            ) : audioPlayer.currentPlayingId === i.id ? (
                              <PauseOutlined />
                            ) : (
                              <IconFont type="icon-voice" />
                            )
                          }
                          onClick={() => {
                            if (audioPlayer.currentPlayingId === i.id) {
                              audioPlayer.stop();
                            } else {
                              const cachedAudio = audioPlayer.getCachedAudio(
                                i.id as string,
                              );
                              audioPlayer.playAudio(
                                i.id as string,
                                cachedAudio,
                                i.message.content as string,
                                synthesizeStream,
                                true,
                              );
                            }
                          }}
                        />
                        <Button
                          type="text"
                          size="small"
                          icon={<CopyOutlined />}
                          onClick={() => copyText(i.message.content as string)}
                        />
                      </Flex>
                    ),
                }),
              // 卡片消息
              ...(i.message.type === 'card' &&
                i.message.data && {
                  content:
                    i.message.data.type === 'alert' ? (
                      <EventCard data={i.message.data.message} />
                    ) : i.message.data.type === 'report' ? (
                      <ReportCard data={i.message.data.message} />
                    ) : (
                      i.message.content
                    ),
                }),
            };
          })}
          style={{
            height: '100%',
          }}
          roles={{
            assistant: {
              placement: 'start',
              // avatar: {
              //   icon: <RobotOutlined />,
              // },
              styles: {
                content: {
                  backgroundColor: 'rgba(3, 120, 61, 0.85)',
                  maxWidth: '70%',
                },
                footer: {
                  color: '#fff',
                },
              },
              loadingRender: () => (
                <Space size={10}>
                  <Spin indicator={<LoadingOutlined spin />} size="small" />
                  思考中…
                </Space>
              ),
            },
            user: {
              placement: 'end',
              styles: {
                content: {
                  backgroundColor: 'rgba(255, 255, 255, 0.85)',
                  color: '#000',
                  maxWidth: '70%',
                },
              },
              messageRender: (content) => {
                return (
                  <div>
                    {/* <IconFont type="icon-voice" style={{ marginRight: 10 }} /> */}
                    {content}
                  </div>
                );
              },
            },
          }}
        />
      </Spin>
    </div>
  );
  const senderHeader = (
    <Sender.Header
      title="Upload File"
      open={attachmentsOpen}
      onOpenChange={setAttachmentsOpen}
      styles={{ content: { padding: 0 } }}
    >
      <Attachments
        beforeUpload={() => false}
        items={attachedFiles}
        onChange={(info) => setAttachedFiles(info.fileList)}
        placeholder={(type) =>
          type === 'drop'
            ? { title: 'Drop file here' }
            : {
                icon: <CloudUploadOutlined />,
                title: 'Upload files',
                description: 'Click or drag files to this area to upload',
              }
        }
      />
    </Sender.Header>
  );

  // 语音识别方式切换开关
  const asrModeSwitch = inputMode === 'voice' && (
    <Flex
      justify="center"
      align="center"
      gap={8}
      style={{
        position: 'absolute',
        right: 10,
        bottom: 10,
      }}
    >
      <span style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.65)' }}>
        Web Speech API
      </span>
      <Switch
        checked={voiceRecognition.currentMode === 'websocket'}
        onChange={(checked) => {
          const newMode = checked ? 'websocket' : 'web-speech';
          voiceRecognition.switchMode(newMode);
        }}
        size="small"
      />
      <Flex align="center" gap={4}>
        {/* 连接状态指示器 */}
        <div
          style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: voiceRecognition.connectionStatus.color,
            boxShadow: `0 0 4px ${voiceRecognition.connectionStatus.color}`,
            transition: 'all 0.3s ease',
          }}
          title={voiceRecognition.connectionStatus.label}
        />
        <span style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.85)' }}>
          WebSocket ASR
        </span>
      </Flex>
    </Flex>
  );

  const chatSender = (
    <Flex className={styles['chat-sender']}>
      {/* 🌟 提示词 */}
      {/* <Prompts
          items={SENDER_PROMPTS}
          onItemClick={(info) => {
            onSubmit(info.data.description as string);
          }}
          styles={{
            item: { padding: '6px 12px' },
          }}
          className={styles.senderPrompt}
        /> */}
      {/* 🌟 输入框 */}
      {inputMode === 'text' ? (
        <div
          className={styles['sender-back']}
          onClick={() => setInputMode('voice')}
        >
          <ArrowLeftOutlined />
        </div>
      ) : (
        // 挡住textarea，阻止输入，因为切换textarea的readonly或disabled会导致移动端键盘不弹出
        <div className={styles['textarea-cover']} />
      )}
      <Sender
        ref={senderRef}
        value={inputValue}
        // header={senderHeader}
        onSubmit={() => {
          onSubmit(inputValue);
          resetInput();
        }}
        onChange={(value) => {
          setInputValue(value);
        }}
        onCancel={() => {
          abortController.current?.abort();
        }}
        // prefix={
        //   <Button
        //     type="text"
        //     icon={<PaperClipOutlined style={{ fontSize: 18 }} />}
        //     onClick={() => setAttachmentsOpen(!attachmentsOpen)}
        //   />
        // }
        loading={agent.isRequesting() || historyMessagesLoading}
        className={
          inputMode === 'text' ? styles['sender'] : styles['sender-voice']
        }
        // allowSpeech - 支持两种方式：WebSocket ASR 或 Web Speech API
        allowSpeech={{
          recording: voiceRecognition.isListening,
          onRecordingChange: async (nextRecording: boolean) => {
            if (nextRecording) {
              // 开始录音
              audioPlayer.stop();
              await voiceRecognition.start();
            } else {
              // 停止录音
              voiceRecognition.stop();
            }
          },
        }}
        actions={(_, info) => {
          const { SendButton, LoadingButton, SpeechButton } = info.components;
          return inputMode === 'text' ? (
            <Flex gap={4}>
              {/* <SpeechButton type="primary" /> */}
              {agent.isRequesting() || historyMessagesLoading ? (
                <LoadingButton type="default" />
              ) : (
                <SendButton type="primary" />
              )}
            </Flex>
          ) : (
            <>
              <div
                className={cn(
                  styles['liquid-button'],
                  styles['liquid-button-voice'],
                  {
                    [styles.active]: voiceRecognition.isListening,
                  },
                )}
                style={{ background: 'none' }}
              >
                <SpeechButton type="primary" />
              </div>
              <div
                className={styles['liquid-button']}
                onClick={() => setInputMode('text')}
              >
                <EditOutlined />
              </div>
            </>
          );
        }}
        placeholder={inputMode === 'text' ? '输入指令或问题' : ''}
        // readOnly={inputMode !== 'text'}
      />
    </Flex>
  );

  const welcome = (
    <Space direction="vertical" size={16} className={styles.welcome}>
      <Welcome
        variant="borderless"
        icon="https://mdn.alipayobjects.com/huamei_iwk9zp/afts/img/A*s5sNRo5LjfQAAAAAAAAAAAAADgCCAQ/fmt.webp"
        title={`你好，我是${config.bot_name}`}
        description="您可以向我下达作业指令，或者问我任何问题"
      />
      {/* <Flex gap={16}>
        <Prompts
          items={[HOT_TOPICS]}
          styles={{
            list: { height: '100%' },
            item: {
              flex: 1,
              backgroundImage:
                'linear-gradient(123deg, #e5f4ff 0%, #efe7ff 100%)',
              borderRadius: 12,
              border: 'none',
            },
            subItem: { padding: 0, background: 'transparent' },
          }}
          onItemClick={(info) => {
            onSubmit(info.data.description as string);
          }}
          className={styles.chatPrompt}
        />

        <Prompts
          items={[DESIGN_GUIDE]}
          styles={{
            item: {
              flex: 1,
              backgroundImage:
                'linear-gradient(123deg, #e5f4ff 0%, #efe7ff 100%)',
              borderRadius: 12,
              border: 'none',
            },
            subItem: { background: '#ffffffa6' },
          }}
          onItemClick={(info) => {
            onSubmit(info.data.description as string);
          }}
          className={styles.chatPrompt}
        />
      </Flex> */}
    </Space>
  );

  // ==================== Render =================
  return (
    <div className={styles['chat-container']}>
      {chatList}
      {chatSender}
      {/* {asrModeSwitch} */}
    </div>
  );
};

export default Independent;
